// The technical facts of one installed device, for the Device Info sheets —
// SUPER ADMIN ONLY (the caller gates it): ids, owner, IP, the firmware
// (FirmwareFacts: last seen, last code updated, the .ino.bin it would download,
// when that bin was published) and the Wi-Fi networks stored IN THE CHIP.
//
// The row is the SERVER's (getDeviceDetailFresh — never the phone cache, which
// cannot know a bench board's OTABinFileId). The chip's networks are asked of
// the device itself (useDeviceWifiList → `getallwifi`), so an offline device
// shows "no answer", never a stale list. Passwords follow the site rule
// (deviceWifi.canSeeWifiPassword) and stay masked until the eye is pressed.

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, IconButton, Tooltip, CircularProgress, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import { getDeviceDetailFresh } from '../../core/api/api';
import { canSeeWifiPassword } from '../../core/devices/deviceWifi';
import { parseServerTime } from '../../core/devices/firmwareInfo';
import useDeviceWifiList from '../../core/hooks/useDeviceWifiList';
import FirmwareFacts from './FirmwareFacts';
import UpdateCodeDialog from './UpdateCodeDialog';
import { useApp } from '../../core/state/AppContext';
import WifiPasswordValue from './WifiPasswordValue';

const FS = 12;
const day = (raw) => {
  const ms = parseServerTime(raw);
  return ms == null ? '—' : new Date(ms).toLocaleDateString(undefined, { dateStyle: 'medium' });
};

function Fact({ label, children }) {
  return (
    <>
      <Typography component="span" sx={{ fontSize: FS, color: 'text.secondary', whiteSpace: 'nowrap' }}>{label}</Typography>
      <Typography component="span" sx={{ fontSize: FS, fontWeight: 600, minWidth: 0, wordBreak: 'break-all', textAlign: 'right' }}>
        {children ?? '—'}
      </Typography>
    </>
  );
}

/**
 * @param uno     the device's unique number
 * @param userId  sender id for `getallwifi`
 * @param who     { isSuperAdmin, userId } for the password rule
 * @param open    only reads while the sheet is open
 * @param inGrid  the caller lays the two blocks (ids + firmware | chip Wi-Fi) out as
 *                columns of ITS grid — so a wide screen needs no scrolling
 */
export default function DeviceTechDetails({
  uno, userId, who, open = true, inGrid = false,
}) {
  const [row, setRow] = useState(null);
  const [readAt, setReadAt] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const { canUpdateFirmware } = useApp();
  const wifi = useDeviceWifiList(uno, userId, open);

  const load = React.useCallback(() => {
    if (!uno) return;
    getDeviceDetailFresh(uno)
      .then((res) => {
        const r = Array.isArray(res.data) ? res.data[0] : null;
        setRow(r || null);
        setReadAt(Date.now());
      })
      .catch(() => {});
  }, [uno]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return () => { setRow(null); setReadAt(null); };
  }, [open, load]);

  const showPass = row ? canSeeWifiPassword(row, who) : false;

  return (
    // display: contents — the two blocks become the caller's grid cells.
    <Box sx={inGrid ? { display: 'contents' } : { mt: 1 }}>
      {!inGrid && <Divider sx={{ mb: 1 }} />}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.25, alignItems: 'baseline',
        }}
        >
          <Fact label="Installed ID">{row?.ID}</Fact>
          <Fact label="Location ID">{row ? `${row.LocationID}${row.LocationName ? ` · ${row.LocationName}` : ''}` : null}</Fact>
          <Fact label="Room ID">{row ? `${row.RoomId ?? row.RoomID}${row.RoomName ? ` · ${row.RoomName}` : ''}` : null}</Fact>
          <Fact label="Owner">{row ? `${row.DeviceMasterUser || row.DeviceMasterUserName || '—'} · ID ${row.MasterUserId}` : null}</Fact>
          <Fact label="Device IP">{row?.DeviceIp || '—'}</Fact>
          <Fact label="Packed on">{row ? day(row.PackingDate) : null}</Fact>
          <Fact label="Bin file ID">{row?.OTABinFileId}</Fact>
        </Box>

        {/* Last seen · last code updated · latest .ino.bin · bin published · chips */}
        {row && <Box sx={{ mt: 1 }}><FirmwareFacts device={row} refreshedAt={readAt} onRefresh={load} onUpdate={canUpdateFirmware ? () => setUpdateOpen(true) : undefined} /></Box>}
        {/* ⚠️ A BUTTON THAT IS ALWAYS THERE (owner, 2026-10-17). The orange
            "Older than the bin" chip already opened this dialog, but nothing said
            it was clickable and it appears only while the device is behind — so
            re-flashing a current device, or one whose dates disagree, had no way
            in from here. Same gate and same dialog as the ⋮ menu. */}
        {row && canUpdateFirmware && (
          <Button
            size="small" variant="contained" color="warning"
            startIcon={<SystemUpdateAltIcon fontSize="small" />}
            onClick={() => setUpdateOpen(true)}
            sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
          >
            Update Code
          </Button>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {!inGrid && <Divider sx={{ my: 1 }} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: FS, fontWeight: 700, flex: 1 }}>Wi-Fi stored in the chip</Typography>
          {wifi.loading && <CircularProgress size={14} />}
          <Tooltip title="Ask the device again (getallwifi)">
            <span>
              <IconButton size="small" onClick={wifi.refresh} disabled={wifi.loading} sx={{ p: 0.25 }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {wifi.silent && (
          <Typography sx={{ fontSize: FS, color: 'warning.main' }}>No answer from the device — it may be offline.</Typography>
        )}
        {wifi.list && !wifi.list.length && (
          <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>The chip holds no networks.</Typography>
        )}
        {(wifi.list || []).map((n) => (
          <Box key={n.ssid} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <WifiIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: FS, fontWeight: 600, flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{n.ssid}</Typography>
            {showPass && <WifiPasswordValue value={n.pass} weight={500} />}
          </Box>
        ))}
      </Box>
      {/* The same Update Code dialog as the ⋮ menu: count, confirm, send (or park). */}
      {row && (
        <UpdateCodeDialog
          open={updateOpen}
          scope={{ type: 'device', device: row, name: row.DeviceName || row.UniqueNumber }}
          onClose={() => { setUpdateOpen(false); load(); }}
        />
      )}
    </Box>
  );
}
